'use client';

import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '../../../stores/workflowStore';

type Mode = 'head' | 'tail' | 'both';

const DEFAULT_N = 50;
const DEFAULT_M = 50;
const MIN_VALUE = 0;

export interface TopNConfigParams {
  n?: number;
  m?: number;
  mode?: Mode;
}

function parseNumber(value: unknown, defaultValue: number): number {
  if (value == null) return defaultValue;
  if (typeof value === 'number' && Number.isFinite(value) && value >= MIN_VALUE) {
    return Math.floor(value);
  }
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed) && parsed >= MIN_VALUE) return parsed;
  }
  return defaultValue;
}

function parseMode(value: unknown): Mode {
  if (value === 'head' || value === 'tail' || value === 'both') {
    return value;
  }
  return 'head';
}

interface TopNConfigProps {
  nodeId: string;
  config: TopNConfigParams;
}

export function TopNConfig({ nodeId, config }: TopNConfigProps) {
  const { t } = useTranslation();
  const { updateNodeConfig } = useWorkflowStore();
  
  const [mode, setMode] = useState<Mode>(parseMode(config.mode));
  const [n, setN] = useState<number>(parseNumber(config.n, DEFAULT_N));
  const [m, setM] = useState<number>(parseNumber(config.m, DEFAULT_M));
  const [nInput, setNInput] = useState(String(parseNumber(config.n, DEFAULT_N)));
  const [mInput, setMInput] = useState(String(parseNumber(config.m, DEFAULT_M)));

  // 当 config 变化时（如 AI 生成工作流后），同步更新内部状态
  useEffect(() => {
    if (config.mode) setMode(parseMode(config.mode));
    if (typeof config.n !== 'undefined') {
      const newN = parseNumber(config.n, DEFAULT_N);
      setN(newN);
      setNInput(String(newN));
    }
    if (typeof config.m !== 'undefined') {
      const newM = parseNumber(config.m, DEFAULT_M);
      setM(newM);
      setMInput(String(newM));
    }
  }, [config.mode, config.n, config.m]);

  const save = (newMode: Mode, newN: number, newM: number) => {
    const clampedN = Math.max(MIN_VALUE, Math.floor(newN));
    const clampedM = Math.max(MIN_VALUE, Math.floor(newM));
    
    setMode(newMode);
    setN(clampedN);
    setM(clampedM);
    setNInput(String(clampedN));
    setMInput(String(clampedM));
    
    updateNodeConfig(nodeId, {
      type: 'top_n',
      params: { 
        mode: newMode,
        n: clampedN,
        m: clampedM,
      },
    });
  };

  const handleModeChange = (newMode: Mode) => {
    save(newMode, n, m);
  };

  const handleNBlur = () => {
    const parsed = parseInt(nInput, 10);
    if (!Number.isNaN(parsed) && parsed >= MIN_VALUE) {
      save(mode, parsed, m);
    } else {
      setNInput(String(n));
    }
  };

  const handleMBlur = () => {
    const parsed = parseInt(mInput, 10);
    if (!Number.isNaN(parsed) && parsed >= MIN_VALUE) {
      save(mode, n, parsed);
    } else {
      setMInput(String(m));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, handler: () => void) => {
    if (e.key === 'Enter') {
      handler();
    }
  };

  const modeOptions = useMemo(() => [
    { value: 'head' as Mode, label: t('nodes.config.top_n.modeHead'), desc: t('nodes.config.top_n.modeHeadDesc') },
    { value: 'tail' as Mode, label: t('nodes.config.top_n.modeTail'), desc: t('nodes.config.top_n.modeTailDesc') },
    { value: 'both' as Mode, label: t('nodes.config.top_n.modeBoth'), desc: t('nodes.config.top_n.modeBothDesc') },
  ], [t]);

  return (
    <div className="space-y-5">
      {/* Mode Selection */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500">
          {t('nodes.config.top_n.modeLabel')}
        </label>
        <div className="space-y-1.5">
          {modeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleModeChange(option.value)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all ${
                mode === option.value
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                mode === option.value ? 'border-emerald-500' : 'border-zinc-300'
              }`}>
                {mode === option.value && (
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                )}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">{option.label}</div>
                <div className={`text-[10px] ${mode === option.value ? 'text-emerald-600' : 'text-zinc-400'}`}>
                  {option.desc}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Head N Input */}
      {(mode === 'head' || mode === 'both') && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-zinc-500">
            {t('nodes.config.top_n.nLabel')}
          </label>
          <input
            type="number"
            min={MIN_VALUE}
            value={nInput}
            onChange={(e) => setNInput(e.target.value)}
            onBlur={handleNBlur}
            onKeyDown={(e) => handleKeyDown(e, handleNBlur)}
            placeholder={t('nodes.config.top_n.nPlaceholder')}
            className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 text-zinc-900 placeholder:text-zinc-400"
          />
          <p className="text-[10px] text-zinc-400">{t('nodes.config.top_n.nHint')}</p>
        </div>
      )}

      {/* Tail M Input */}
      {(mode === 'tail' || mode === 'both') && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-zinc-500">
            {t('nodes.config.top_n.mLabel')}
          </label>
          <input
            type="number"
            min={MIN_VALUE}
            value={mInput}
            onChange={(e) => setMInput(e.target.value)}
            onBlur={handleMBlur}
            onKeyDown={(e) => handleKeyDown(e, handleMBlur)}
            placeholder={t('nodes.config.top_n.mPlaceholder')}
            className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 text-zinc-900 placeholder:text-zinc-400"
          />
          <p className="text-[10px] text-zinc-400">{t('nodes.config.top_n.mHint')}</p>
        </div>
      )}
    </div>
  );
}
